import React, { useEffect, useState, useCallback } from "react";
import {
  Database, Globe, Server, Cpu, CheckCircle2,
  Mail, Bot, Eye, EyeOff, Save, Loader2, FlaskConical, RotateCcw,
  ToggleLeft, ToggleRight, CreditCard, Truck,
  DownloadCloud, RefreshCw, XCircle, CalendarClock,
} from "lucide-react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const DEFAULT_SYSTEM_PROMPT =
  "You are the GOT PARTS virtual assistant. GOT PARTS is a B2B e-commerce platform that " +
  "sells replacement parts for commercial / restaurant kitchen and facility equipment " +
  "(plumbing parts, oven parts, coffee machine parts, beverage, dishwashing, water filtration, etc.).\n\n" +
  "STRICT RULES:\n" +
  "1. ONLY answer questions related to this website: finding and searching parts, the catalogue and " +
  "categories, product availability/stock, how to place an order, how to request a quote, customer " +
  "registration/account, order status, favourites, and general buying help on this site.\n" +
  "2. If the user asks anything unrelated (general knowledge, coding, personal advice, other companies, " +
  "politics, etc.), politely decline in ONE sentence and steer them back to GOT PARTS, offering to " +
  "connect them with a human agent.\n" +
  "3. You never invent prices. If a price is not provided in the context, tell the user to add the item " +
  "to the cart and request a quote.\n" +
  "4. Be concise, professional and helpful. Reply in the SAME language the customer used.\n" +
  "5. If you don't know or it needs a human (complex order issue, refund, custom request), say you'll " +
  "connect them with the team.\n";

// ── shared primitives ─────────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs font-medium text-steel-light uppercase tracking-wider">{label}</label>
    {hint && <p className="text-[11px] text-steel-light mt-0.5 mb-1">{hint}</p>}
    <div className="mt-1">{children}</div>
  </div>
);

const Inp = ({ className = "", ...props }) => (
  <input className={`w-full h-10 px-3 border border-border rounded-md text-sm outline-none focus:border-orange bg-white ${className}`} {...props} />
);

const Sel = ({ children, ...props }) => (
  <select className="w-full h-10 px-3 border border-border rounded-md text-sm outline-none focus:border-orange bg-white" {...props}>{children}</select>
);

const SecretInp = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Inp type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-steel-light hover:text-graphite">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Toggle = ({ checked, onChange, label }) => (
  <button type="button" onClick={() => onChange(!checked)} className={`flex items-center gap-2 text-sm font-medium ${checked ? "text-orange" : "text-steel"}`}>
    {checked ? <ToggleRight className="w-8 h-8 text-orange" /> : <ToggleLeft className="w-8 h-8 text-steel-light" />}
    {label}
  </button>
);

const SectionCard = ({ icon: Icon, title, badge, children }) => (
  <div className="bg-white border border-border rounded-lg p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-orange" />
      <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light">{title}</div>
      {badge}
    </div>
    {children}
  </div>
);

const SaveBtn = ({ saving, onClick, label = "Save" }) => (
  <button onClick={onClick} disabled={saving} className="inline-flex items-center gap-2 bg-graphite hover:bg-orange text-white text-sm font-medium rounded-md px-4 h-9 transition-colors disabled:opacity-60">
    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {label}
  </button>
);

// ── Email ─────────────────────────────────────────────────────────────────────
const EmailSection = () => {
  const [cfg, setCfg] = useState({ host: "", port: 587, username: "", password: "", from_name: "GOT PARTS", from_email: "", notification_email: "", use_tls: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { client.get("/admin/settings/email").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async () => { setSaving(true); try { await client.post("/admin/settings/email", cfg); toast.success("Email configuration saved."); } catch { toast.error("Failed to save."); } finally { setSaving(false); } };
  const test = async () => { setTesting(true); try { const { data } = await client.post("/admin/settings/email/test", cfg); data.ok ? toast.success(`Test email sent to ${data.sent_to}`) : toast.error(`Test failed: ${data.error}`); } catch { toast.error("Test request failed."); } finally { setTesting(false); } };

  return (
    <SectionCard icon={Mail} title="Email Configuration">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="SMTP Host"><Inp value={cfg.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.gmail.com" /></Field>
        <Field label="SMTP Port"><Inp type="number" value={cfg.port} onChange={(e) => set("port", Number(e.target.value))} placeholder="587" /></Field>
        <Field label="Username / Login Email"><Inp type="email" value={cfg.username} onChange={(e) => set("username", e.target.value)} placeholder="user@domain.com" /></Field>
        <Field label="Password"><SecretInp value={cfg.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" /></Field>
        <Field label="From Name"><Inp value={cfg.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="GOT PARTS" /></Field>
        <Field label="From Email"><Inp type="email" value={cfg.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="noreply@gotparts.ca" /></Field>
        <Field label="Admin Notification Email" hint="Receives alerts for new orders and quotes.">
          <Inp type="email" value={cfg.notification_email} onChange={(e) => set("notification_email", e.target.value)} placeholder="admin@gotparts.ca" />
        </Field>
        <div className="flex items-end pb-2"><Toggle checked={cfg.use_tls} onChange={(v) => set("use_tls", v)} label="Use STARTTLS" /></div>
      </div>
      <div className="mt-5 flex gap-2">
        <SaveBtn saving={saving} onClick={save} />
        <button onClick={test} disabled={testing || !cfg.host} className="inline-flex items-center gap-2 bg-surface border border-border hover:border-orange text-graphite text-sm font-medium rounded-md px-4 h-9 transition-colors disabled:opacity-60">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Send Test Email
        </button>
      </div>
    </SectionCard>
  );
};

// ── AI ────────────────────────────────────────────────────────────────────────
const GEMINI_MODELS = ["gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash-exp"];

const AISection = () => {
  const [cfg, setCfg] = useState({ enabled: false, provider: "gemini", api_key: "", has_api_key: false, model: "gemini-1.5-flash", system_prompt: DEFAULT_SYSTEM_PROMPT, max_tokens: 512 });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testReply, setTestReply] = useState(null);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { client.get("/admin/settings/ai").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async () => { setSaving(true); try { const { data } = await client.post("/admin/settings/ai", cfg); setCfg(data); toast.success("AI configuration saved."); } catch { toast.error("Failed to save."); } finally { setSaving(false); } };
  const test = async () => { setTesting(true); setTestReply(null); try { const { data } = await client.post("/admin/settings/ai/test", cfg); data.ok ? setTestReply(data.reply) : toast.error(`Test failed: ${data.error}`); } catch { toast.error("Test request failed."); } finally { setTesting(false); } };

  const apiKeyPlaceholder = cfg.has_api_key && !cfg.api_key
    ? "Key saved — enter a new value to replace"
    : (cfg.provider === "gemini" ? "AIza… or AQ.…" : "sk-ant-…");

  return (
    <SectionCard icon={Bot} title="AI Chat Bot" badge={<span className={`ml-auto text-xs mono px-2 py-0.5 rounded-full border ${cfg.enabled ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-steel-light border-border bg-surface"}`}>{cfg.enabled ? "Active" : "Inactive"}</span>}>
      <div className="space-y-4">
        <Toggle checked={cfg.enabled} onChange={(v) => set("enabled", v)} label={cfg.enabled ? "AI bot responds when agent is offline" : "AI bot disabled — offline message only"} />
        {cfg.enabled && (
          <>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <Field label="Provider">
                <Sel value={cfg.provider} onChange={(e) => set("provider", e.target.value)}>
                  <option value="gemini">Google Gemini</option>
                  <option value="anthropic">Anthropic Claude</option>
                </Sel>
              </Field>
              <Field label="Model">
                {cfg.provider === "gemini"
                  ? <Sel value={cfg.model} onChange={(e) => set("model", e.target.value)}>{GEMINI_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}</Sel>
                  : <Sel value={cfg.model} onChange={(e) => set("model", e.target.value)}><option value="claude-haiku-4-5-20251001">claude-haiku-4-5 (fast)</option><option value="claude-sonnet-4-6">claude-sonnet-4-6 (balanced)</option></Sel>}
              </Field>
              <Field label="API Key" hint={cfg.has_api_key && !cfg.api_key ? "A key is saved. Leave blank to keep it, or type a new one to replace." : undefined}>
                <SecretInp value={cfg.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder={apiKeyPlaceholder} />
              </Field>
              <Field label="Max Tokens" hint="Max length of each bot reply."><Inp type="number" min={64} max={2048} value={cfg.max_tokens} onChange={(e) => set("max_tokens", Number(e.target.value))} /></Field>
            </div>
            <Field label="System Prompt" hint="Defines the bot's behaviour. Keep it scoped to GOT PARTS topics.">
              <div className="relative">
                <textarea rows={9} value={cfg.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} className="w-full px-3 py-2 border border-border rounded-md text-sm outline-none focus:border-orange bg-white font-mono resize-y" />
                <button type="button" onClick={() => set("system_prompt", DEFAULT_SYSTEM_PROMPT)} className="absolute top-2 right-2 text-xs flex items-center gap-1 text-steel-light hover:text-orange"><RotateCcw className="w-3 h-3" /> Reset</button>
              </div>
            </Field>
            {testReply && <div className="bg-surface border border-border rounded-md p-3 text-sm text-graphite"><div className="mono text-[10px] text-steel-light uppercase tracking-wider mb-1">Bot reply preview</div><p className="whitespace-pre-wrap leading-relaxed">{testReply}</p></div>}
            <button onClick={test} disabled={testing || !cfg.api_key} className="inline-flex items-center gap-2 bg-surface border border-border hover:border-orange text-graphite text-sm font-medium rounded-md px-4 h-9 transition-colors disabled:opacity-60">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Test AI
            </button>
          </>
        )}
      </div>
      <div className="mt-5"><SaveBtn saving={saving} onClick={save} label="Save AI Settings" /></div>
    </SectionCard>
  );
};

// ── Stripe ────────────────────────────────────────────────────────────────────
const StripeSection = () => {
  const [cfg, setCfg] = useState({ enabled: false, mode: "test", publishable_key: "", secret_key: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { client.get("/admin/settings/stripe").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => { setSaving(true); try { await client.post("/admin/settings/stripe", cfg); toast.success("Stripe configuration saved."); } catch { toast.error("Failed to save."); } finally { setSaving(false); } };

  return (
    <SectionCard icon={CreditCard} title="Stripe — B2C Payments" badge={<span className={`ml-auto text-xs mono px-2 py-0.5 rounded-full border ${cfg.enabled ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-steel-light border-border bg-surface"}`}>{cfg.enabled ? `Active (${cfg.mode})` : "Not connected"}</span>}>
      <div className="space-y-4">
        <Toggle checked={cfg.enabled} onChange={(v) => set("enabled", v)} label={cfg.enabled ? "Stripe payments enabled for B2C checkout" : "Stripe payments disabled"} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Mode">
            <Sel value={cfg.mode} onChange={(e) => set("mode", e.target.value)}>
              <option value="test">Test</option>
              <option value="live">Live</option>
            </Sel>
          </Field>
          <div />
          <Field label="Publishable Key" hint={cfg.mode === "test" ? "Starts with pk_test_" : "Starts with pk_live_"}>
            <Inp value={cfg.publishable_key} onChange={(e) => set("publishable_key", e.target.value)} placeholder={cfg.mode === "test" ? "pk_test_…" : "pk_live_…"} />
          </Field>
          <Field label="Secret Key" hint={cfg.mode === "test" ? "Starts with sk_test_" : "Starts with sk_live_"}>
            <SecretInp value={cfg.secret_key} onChange={(e) => set("secret_key", e.target.value)} placeholder={cfg.mode === "test" ? "sk_test_…" : "sk_live_…"} />
          </Field>
          <Field label="Webhook Secret" hint="From Stripe Dashboard → Webhooks → Signing secret.">
            <SecretInp value={cfg.webhook_secret} onChange={(e) => set("webhook_secret", e.target.value)} placeholder="whsec_…" />
          </Field>
        </div>
        <div className="text-xs text-steel-light bg-surface border border-border rounded p-3">
          Stripe is not yet connected to the checkout flow. Save your keys here so they are ready when the B2C checkout is activated.
        </div>
      </div>
      <div className="mt-5"><SaveBtn saving={saving} onClick={save} label="Save Stripe Settings" /></div>
    </SectionCard>
  );
};

// ── GARLAND ───────────────────────────────────────────────────────────────────
const GarlandSection = () => {
  const [cfg, setCfg] = useState({ enabled: false, api_url: "", api_key: "", account_number: "", carrier_name: "GARLAND" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { client.get("/admin/settings/garland").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => { setSaving(true); try { await client.post("/admin/settings/garland", cfg); toast.success("GARLAND configuration saved."); } catch { toast.error("Failed to save."); } finally { setSaving(false); } };

  return (
    <SectionCard icon={Truck} title="GARLAND — Shipping & Tracking" badge={<span className={`ml-auto text-xs mono px-2 py-0.5 rounded-full border ${cfg.enabled ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-steel-light border-border bg-surface"}`}>{cfg.enabled ? "Connected" : "Not connected"}</span>}>
      <div className="space-y-4">
        <Toggle checked={cfg.enabled} onChange={(v) => set("enabled", v)} label={cfg.enabled ? "GARLAND tracking active" : "GARLAND tracking disabled"} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Carrier Name"><Inp value={cfg.carrier_name} onChange={(e) => set("carrier_name", e.target.value)} placeholder="GARLAND" /></Field>
          <Field label="Account Number"><Inp value={cfg.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="Your GARLAND account number" /></Field>
          <Field label="API URL" hint="GARLAND's tracking API base URL.">
            <Inp value={cfg.api_url} onChange={(e) => set("api_url", e.target.value)} placeholder="https://api.garland.com/v1" />
          </Field>
          <Field label="API Key"><SecretInp value={cfg.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="Your GARLAND API key" /></Field>
        </div>
        <div className="text-xs text-steel-light bg-surface border border-border rounded p-3">
          When enabled, customers can track their shipment via <code className="mono">/api/track/&#123;tracking_number&#125;</code>. A tracking number field will be added to the order update flow.
        </div>
      </div>
      <div className="mt-5"><SaveBtn saving={saving} onClick={save} label="Save GARLAND Settings" /></div>
    </SectionCard>
  );
};

// ── Import & Sync (inlined) ───────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = { completed: ["bg-emerald-50 text-emerald-700 border-emerald-200", CheckCircle2], running: ["bg-amber-50 text-amber-700 border-amber-200", Loader2], failed: ["bg-red-50 text-red-700 border-red-200", XCircle] };
  const [cls, Icon] = map[status] || map.failed;
  return <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded px-2 py-0.5 ${cls}`}><Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} />{status}</span>;
};

const LogTable = ({ logs }) => (
  <div className="border border-border rounded-lg overflow-hidden bg-white">
    <table className="w-full text-sm">
      <thead className="bg-surface text-steel-light"><tr className="text-left mono text-xs uppercase tracking-wider"><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Added</th><th className="px-4 py-2.5">Updated</th><th className="px-4 py-2.5">Unchanged</th><th className="px-4 py-2.5">Cats/Brands</th><th className="px-4 py-2.5">Started</th></tr></thead>
      <tbody>
        {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-steel-light">No runs yet.</td></tr>}
        {logs.map((l) => (
          <tr key={l.id} className="border-t border-border">
            <td className="px-4 py-3"><StatusBadge status={l.status} />{l.error && <div className="text-xs text-red-600 mt-1 max-w-xs truncate">{l.error}</div>}</td>
            <td className="px-4 py-3 mono text-emerald-600">{l.added}</td>
            <td className="px-4 py-3 mono text-orange">{l.updated}</td>
            <td className="px-4 py-3 mono text-steel-light">{l.unchanged}</td>
            <td className="px-4 py-3 mono text-steel">{l.categories}/{l.brands}</td>
            <td className="px-4 py-3 mono text-xs text-steel-light">{l.started_at ? new Date(l.started_at).toLocaleString() : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CatalogueTab = () => {
  const [importLogs, setImportLogs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [busy, setBusy] = useState("");
  const [activeLog, setActiveLog] = useState("import");

  const load = useCallback(() => {
    client.get("/admin/import-logs").then((r) => setImportLogs(r.data.items));
    client.get("/admin/sync-logs").then((r) => setSyncLogs(r.data.items));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const run = async (type) => {
    setBusy(type);
    try {
      await client.post(`/admin/${type}`, null, { params: type === "import" ? { limit: 150 } : { limit: 300 } });
      toast.success(`${type === "import" ? "Import" : "Sync"} started`, { description: "Reading public catalogue from gotparts.ca…" });
      setTimeout(load, 1500);
    } catch { toast.error("Failed to start"); } finally { setTimeout(() => setBusy(""), 2000); }
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-1"><DownloadCloud className="w-5 h-5 text-orange" /><span className="font-semibold text-graphite">Import Catalogue</span></div>
          <p className="text-sm text-steel-light mb-4">Discover and import products, categories and brands from the source website.</p>
          <Button onClick={() => run("import")} disabled={!!busy} className="w-full h-11 bg-orange hover:bg-orange-hover">
            {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DownloadCloud className="w-4 h-4 mr-2" />} Run Import
          </Button>
        </div>
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-1"><RefreshCw className="w-5 h-5 text-orange" /><span className="font-semibold text-graphite">Sync Catalogue</span></div>
          <p className="text-sm text-steel-light mb-4">Detect new products, update descriptions, images, stock, prices & attributes.</p>
          <Button onClick={() => run("sync")} disabled={!!busy} variant="outline" className="w-full h-11 border-graphite">
            {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Run Sync
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-steel-light bg-surface border border-border rounded-md px-4 py-2.5">
        <CalendarClock className="w-4 h-4 text-orange" /> Scheduled daily sync is planned — run manually for now.
      </div>
      <div className="flex gap-2 border-b border-border pb-0">
        {["import", "sync"].map((t) => (
          <button key={t} onClick={() => setActiveLog(t)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeLog === t ? "border-orange text-orange" : "border-transparent text-steel hover:text-graphite"}`}>
            {t === "import" ? "Import Logs" : "Sync Logs"}
          </button>
        ))}
      </div>
      <LogTable logs={activeLog === "import" ? importLogs : syncLogs} />
    </div>
  );
};

// ── System Health ─────────────────────────────────────────────────────────────
const SystemTab = () => {
  const [h, setH] = useState(null);
  useEffect(() => { client.get("/admin/health").then((r) => setH(r.data)).catch(() => {}); }, []);

  const Row = ({ icon: Icon, label, value, ok }) => (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-2.5 text-steel"><Icon className="w-4 h-4 text-steel-light" /> {label}</div>
      <span className={`mono text-sm ${ok === false ? "text-red-600" : ok ? "text-emerald-600" : "text-graphite"}`}>{value}</span>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-border rounded-lg p-5">
        <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">System Health</div>
        <Row icon={Database} label="Database" value={h?.database || "…"} ok={h?.database === "online"} />
        <Row icon={Globe} label="Source Website" value={h ? new URL(h.source_website).host : "…"} />
        <Row icon={Server} label="Store API" value={h ? "reachable" : "…"} ok={!!h} />
        <Row icon={Cpu} label="Products Indexed" value={h?.products_indexed ?? "…"} />
      </div>
      <div className="bg-white border border-border rounded-lg p-5">
        <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Planned Integrations</div>
        <div className="space-y-2">
          {h?.future_integrations?.map((f) => (
            <div key={f.name} className="flex items-center justify-between text-sm">
              <span className="text-steel">{f.name}</span>
              <span className="mono text-xs bg-surface border border-border rounded px-2 py-0.5 text-steel-light capitalize">{f.status}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-steel-light bg-surface rounded-md p-3">
          <CheckCircle2 className="w-4 h-4 text-orange shrink-0 mt-0.5" /> API placeholder ready — NetSuite, Smart Bins, QR codes, barcode scanning and warehouse picking can plug into the existing data model.
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const Settings = () => (
  <div>
    <h1 className="text-2xl font-bold tracking-tight text-graphite">Settings</h1>
    <p className="text-sm text-steel-light mt-1 mb-6">Configure email, AI chat, payments, shipping, catalogue sync and system health.</p>
    <Tabs defaultValue="integrations">
      <TabsList className="mb-6">
        <TabsTrigger value="integrations">Integrations</TabsTrigger>
        <TabsTrigger value="catalogue">Catalogue</TabsTrigger>
        <TabsTrigger value="system">System</TabsTrigger>
      </TabsList>
      <TabsContent value="integrations" className="space-y-5">
        <EmailSection />
        <AISection />
        <StripeSection />
        <GarlandSection />
      </TabsContent>
      <TabsContent value="catalogue">
        <CatalogueTab />
      </TabsContent>
      <TabsContent value="system">
        <SystemTab />
      </TabsContent>
    </Tabs>
  </div>
);

export default Settings;
